import {
  HEALTH_JOURNEY_COMMON_ASSET_KEYS,
  HealthActivityModal
} from "./chunk-2YTABB4B.js";
import {
  __async,
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-LS752DCC.js";

// src/index.ts
import React20 from "react";

// src/init-module.ts
import { setDefaultAssets } from "@leagueplatform/asset-config";

// src/components/activities-cycling-animation.component.tsx
import React from "react";
import { useIntl } from "@leagueplatform/locales";
import {
  useLottieAnimation
} from "@leagueplatform/lottie";
import { Box, VisuallyHidden } from "@leagueplatform/genesis-core";
import { usePrefersReducedMotion } from "@leagueplatform/web-common";
var animationData = import("./league-cycling-animation.lottie-FJOA4SI6.js");
var CycleAnimation = ({
  lottieConfig: configOverrides,
  shouldPlay,
  setShouldPlay
}) => {
  const { formatMessage } = useIntl();
  const prefersReducedMotion = usePrefersReducedMotion();
  const lottieConfig = __spreadValues({
    autoplay: true,
    loop: true
  }, configOverrides);
  const { animation, animationContainerRef } = useLottieAnimation({
    lottieConfig,
    animationData
  });
  animation == null ? void 0 : animation.setSpeed(prefersReducedMotion ? 0.25 : 1);
  const handleAnimationPlayback = () => {
    if (animation == null ? void 0 : animation.isPaused) {
      setShouldPlay == null ? void 0 : setShouldPlay(true);
      animation == null ? void 0 : animation.play();
    } else {
      setShouldPlay == null ? void 0 : setShouldPlay(false);
      animation == null ? void 0 : animation.pause();
    }
  };
  return /* @__PURE__ */ React.createElement(
    Box,
    {
      ref: animationContainerRef,
      onClick: handleAnimationPlayback,
      css: { opacity: shouldPlay ? 1 : 0.5 }
    },
    /* @__PURE__ */ React.createElement(VisuallyHidden, null, formatMessage({ id: "ANIMATION_ACCESSIBILITY_PLAYBACK" }))
  );
};

// src/asset-map.ts
import emptyStateConfigurableActivities from "././assets/empty-state-configurable-activities.png";
import enabledBadgePlaceholder from "././assets/enabled-badge-placeholder.svg";
import completedProgram from "././assets/completedProgram.png";
import programHeadingBannerIcon from "././assets/programHeadingBannerIcon.png";
import removeProgramIcon from "././assets/remove-program.png";
var HEALTH_JOURNEY_COMMON_ASSET_MAP = {
  EMPTY_STATE_CONFIGURABLE_ACTIVITIES: emptyStateConfigurableActivities,
  ENABLED_BADGE_PLACEHOLDER: enabledBadgePlaceholder,
  COMPLETED_PROGRAM: completedProgram,
  PROGRAM_HEADING_BANNER_ICON: programHeadingBannerIcon,
  ACTIVITIES_CYCLING_ANIMATION: CycleAnimation,
  REMOVE_PROGRAM_IMAGE: removeProgramIcon
};

// src/init-module.ts
var initModule = () => {
  setDefaultAssets(HEALTH_JOURNEY_COMMON_ASSET_MAP);
};

// src/health-journey-routes.ts
import { getModulePath, LEAGUE_MODULE_NAMES } from "@leagueplatform/core";

// src/utils/capability-routing.utils.ts
var getLocationWithReferrer = (_a) => {
  var _b = _a, {
    pathname,
    state = {}
  } = _b, locationParams = __objRest(_b, [
    "pathname",
    "state"
  ]);
  var _a2;
  return __spreadProps(__spreadValues({}, locationParams), {
    pathname,
    state: __spreadProps(__spreadValues({}, state), {
      referrerStack: ((_a2 = state == null ? void 0 : state.referrerStack) != null ? _a2 : []).concat(
        `${window.location.pathname || ""}${window.location.search || ""}`
      ).filter(Boolean)
    })
  });
};

// src/health-journey-routes.ts
var JOURNEY_ROUTES = {
  memberHome: "/member/home",
  journeyHome: () => getModulePath(LEAGUE_MODULE_NAMES.healthJourney),
  journeyHistoryHome: () => getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory),
  journeySuggested: () => `${getModulePath(LEAGUE_MODULE_NAMES.healthJourney)}/suggested`,
  programsHome: () => getModulePath(LEAGUE_MODULE_NAMES.healthPrograms),
  progressHome: () => getModulePath(LEAGUE_MODULE_NAMES.healthProgress),
  achievementsHome: () => getModulePath(LEAGUE_MODULE_NAMES.rewardsAchievements),
  challengesHome: () => `${getModulePath(LEAGUE_MODULE_NAMES.healthJourney)}/challenges`,
  getActivityDetails: (activityId, type, locationParams) => type === "healthJourney_toolboxActivity" ? JOURNEY_ROUTES.getToolboxActivityDetails(activityId, locationParams) : getLocationWithReferrer(__spreadProps(__spreadValues({}, locationParams), {
    pathname: `${JOURNEY_ROUTES.journeyHome()}/activity/${activityId}`
  })),
  getToolboxActivityDetails: (activityId, locationParams) => getLocationWithReferrer(__spreadProps(__spreadValues({}, locationParams), {
    pathname: `${JOURNEY_ROUTES.journeyHome()}/activity/${activityId}/toolbox`
  })),
  getActivitySteps: (activityId, type, locationParams) => type === "healthJourney_toolboxActivity" ? JOURNEY_ROUTES.getToolboxActivityDetails(activityId, locationParams) : getLocationWithReferrer(__spreadProps(__spreadValues({}, locationParams), {
    pathname: `${JOURNEY_ROUTES.journeyHome()}/activity/${activityId}/steps`
  })),
  getSuggestedActivity: (activityId) => `${JOURNEY_ROUTES.journeySuggested()}/${activityId}`,
  getChallengeDetails: (challengeId) => `${JOURNEY_ROUTES.challengesHome()}/${challengeId}`,
  getChallengeSettings: (challengeId) => `${JOURNEY_ROUTES.challengesHome()}/${challengeId}/settings`,
  getChallengeTeamDetails: (challengeId, teamId) => `${JOURNEY_ROUTES.challengesHome()}/${challengeId}/team/${teamId}`,
  getChallengeJoinTeam: (challengeId) => `${JOURNEY_ROUTES.challengesHome()}/${challengeId}/join-team`,
  getChallengeCompletedActivities: (challengeId) => `${JOURNEY_ROUTES.challengesHome()}/${challengeId}/activities/completed`,
  getChallengeMissedActivities: (challengeId) => `${JOURNEY_ROUTES.challengesHome()}/${challengeId}/activities/missed`,
  getProgramDetails: (programId) => `${JOURNEY_ROUTES.programsHome()}/${programId}`,
  getProgramCategory: (categoryId) => `${JOURNEY_ROUTES.programsHome()}/category/${categoryId}`,
  getCompletedActivityHistory: () => `${JOURNEY_ROUTES.progressHome()}/completed-history`,
  getMissedActivityHistory: () => `${JOURNEY_ROUTES.progressHome()}/missed-history`,
  getStandaloneActivities: () => `${JOURNEY_ROUTES.progressHome()}/standalone`,
  getAllChallengeType: () => `${JOURNEY_ROUTES.programsHome()}/all-challenges`,
  getRewardProgramOverview: (userHealthCampaignId) => `${getModulePath(
    LEAGUE_MODULE_NAMES.healthJourney
  )}/reward-program/${userHealthCampaignId}`,
  getRewardProgramProgress: (userHealthCampaignId) => `${getModulePath(
    LEAGUE_MODULE_NAMES.healthJourney
  )}/reward-program/${userHealthCampaignId}?page=progress`,
  getCareLibrary: () => `/care-library`
};

// src/components/program-navigation-tabs/program-tabs.component.tsx
import * as React2 from "react";
import { genesisStyled, css } from "@leagueplatform/genesis-commons";
import { useEffect, useState } from "react";
import {
  Tabs,
  queryHelpers,
  useMediaQuery
} from "@leagueplatform/genesis-core";
var TabsStyled = genesisStyled(Tabs.Root)(
  () => css({
    width: "100%"
  })
);
var TabListStyled = genesisStyled(Tabs.TabList)(
  () => css({
    borderRadius: ["none", "small"],
    borderColor: "onSurface.border.subdued",
    borderWidth: ["none", "thin"],
    borderBottomWidth: "thin",
    borderStyle: "solid",
    paddingX: [0, "oneAndHalf"],
    height: ["36px", "56px"],
    width: "100%"
  })
);
var TabItemStyled = genesisStyled(Tabs.Tab)(
  () => css({
    fontSize: "subtitle1",
    fontWeight: 600,
    fontStyle: "normal",
    "&::after": {
      height: ["2px", "3px"],
      borderStartStartRadius: [0, "small"],
      borderStartEndRadius: [0, "small"]
    }
  })
);
var ProgramTabs = ({ tabs, currentTab }) => {
  const isMobile = useMediaQuery(queryHelpers.down("laptop"));
  const [tabIndex, setTabIndex] = useState(0);
  useEffect(() => {
    const index = tabs.findIndex((tab) => tab.tabKey === currentTab);
    if (index >= 0) {
      setTabIndex(index);
    }
  }, [currentTab, tabs]);
  const onTabClick = (nextIndex) => {
    const { onClick = () => {
    } } = tabs[nextIndex];
    if (nextIndex !== tabIndex) {
      onClick();
    }
  };
  return /* @__PURE__ */ React2.createElement(TabsStyled, { value: tabIndex.toString() }, /* @__PURE__ */ React2.createElement(TabListStyled, { tabDistribution: "hugContents" }, tabs.map((tab, index) => /* @__PURE__ */ React2.createElement(
    TabItemStyled,
    {
      onClick: () => onTabClick(index),
      value: index.toString(),
      css: { flexGrow: isMobile ? 1 : 0 },
      key: `${tab.tabName}-tab`
    },
    tab.tabName
  ))), tabs.map((tab, index) => {
    const tabContent = React2.cloneElement(tab.tabContent, {
      isVisible: index === tabIndex
    });
    return /* @__PURE__ */ React2.createElement(
      Tabs.TabPanel,
      {
        css: { padding: "$none" },
        value: index.toString(),
        key: `${tab.tabName}-panel`
      },
      tabContent
    );
  }));
};

// src/components/program-layout/program-layout.component.tsx
import * as React3 from "react";
import {
  StackLayout,
  StackItem,
  Responsive
} from "@leagueplatform/genesis-core";
import { BackButtonArrow } from "@leagueplatform/web-common-components";
import { useNavigateLegacy as useNavigate } from "@leagueplatform/routing";

// src/constants/program.constants.ts
var PROGRAM_BACK_BUTTON_WIDTH = "52px";
var PROGRAM_CONTENT_WIDTH = "778px";
var PROGRAM_SIDEBAR_WIDTH = "357px";
var PROGRAM_SIDEBAR_HEIGHT = "200px";
var CHALLENGE_TYPE_GROUP = "group";
var CHALLENGE_TYPE_SOLO = "solo";
var CHALLENGE = "challenge";
var CHALLENGE_TYPES = [
  CHALLENGE_TYPE_SOLO,
  CHALLENGE_TYPE_GROUP
];

// src/components/program-layout/program-layout.component.tsx
var ProgramLayout = ({
  header,
  content,
  sidebar
}) => {
  const navigate = useNavigate();
  return /* @__PURE__ */ React3.createElement(
    StackLayout,
    {
      css: {
        marginBottom: "$three",
        padding: "$twoAndHalf"
      },
      orientation: {
        "@initial": "vertical",
        "@laptop": "horizontal",
        "@desktop": "horizontal"
      },
      horizontalAlignment: "stretch"
    },
    /* @__PURE__ */ React3.createElement(StackLayout, { spacing: "$oneAndHalf", horizontalAlignment: "stretch" }, header && /* @__PURE__ */ React3.createElement(
      StackLayout,
      {
        orientation: "horizontal",
        spacing: "$four",
        css: { width: "100%", justifyContent: "center" }
      },
      /* @__PURE__ */ React3.createElement(
        StackLayout,
        {
          orientation: {
            "@initial": "vertical",
            "@laptop": "horizontal",
            "@desktop": "horizontal"
          },
          css: {
            maxWidth: PROGRAM_CONTENT_WIDTH
          }
        },
        /* @__PURE__ */ React3.createElement(
          StackItem,
          {
            css: {
              width: "100%",
              "@desktop": { width: "auto" },
              "@laptop": { width: "auto" }
            }
          },
          /* @__PURE__ */ React3.createElement(
            BackButtonArrow,
            {
              justifyContent: "initial",
              marginInlineEnd: "quarter",
              marginY: "half",
              onClick: () => {
                navigate(-1);
              },
              size: "three"
            }
          )
        ),
        header
      ),
      sidebar && /* @__PURE__ */ React3.createElement(Responsive, { up: "laptop" }, /* @__PURE__ */ React3.createElement(
        StackItem,
        {
          css: {
            width: PROGRAM_SIDEBAR_WIDTH,
            maxHeight: PROGRAM_SIDEBAR_HEIGHT,
            overflowY: "visible"
          }
        },
        sidebar
      ))
    ), /* @__PURE__ */ React3.createElement(
      StackLayout,
      {
        orientation: "horizontal",
        horizontalAlignment: "center",
        spacing: "$four"
      },
      /* @__PURE__ */ React3.createElement(
        StackItem,
        {
          css: {
            width: "100%",
            maxWidth: PROGRAM_CONTENT_WIDTH,
            paddingInlineStart: 0,
            "@laptop": {
              paddingInlineStart: PROGRAM_BACK_BUTTON_WIDTH
            },
            "@desktop": {
              paddingInlineStart: PROGRAM_BACK_BUTTON_WIDTH
            }
          }
        },
        content
      ),
      sidebar && /* @__PURE__ */ React3.createElement(Responsive, { up: "laptop" }, /* @__PURE__ */ React3.createElement(StackItem, { css: { width: PROGRAM_SIDEBAR_WIDTH } }, !header && sidebar))
    ))
  );
};

// src/components/program-heading/program-heading.component.tsx
import React4 from "react";
import { handleStaticAsset } from "@leagueplatform/asset-config";
import {
  StackLayout as StackLayout2,
  HeadingText,
  useMediaQuery as useMediaQuery2,
  queryHelpers as queryHelpers2,
  StatusBanner,
  Icon
} from "@leagueplatform/genesis-core";
import { CoverImage } from "@leagueplatform/genesis-commons";
import { Badge } from "@leagueplatform/web-common-components";
var PROGRAM_HEADING_FIXED_HEIGHT = { _: 233, phone: 377, tablet: 437 };
var ProgramHeading = ({
  bannerText,
  image,
  imageAltText,
  title,
  badge,
  settingsComponent,
  children
}) => {
  const isMobile = useMediaQuery2(queryHelpers2.down("mobileLandscape"));
  const centerAlignHeadingItems = isMobile && !settingsComponent;
  const programHeadingBannerIcon2 = handleStaticAsset(
    "PROGRAM_HEADING_BANNER_ICON" /* PROGRAM_HEADING_BANNER_ICON */
  );
  return /* @__PURE__ */ React4.createElement(
    StackLayout2,
    {
      as: "header",
      spacing: "$one",
      horizontalAlignment: "stretch",
      css: { width: "100%" }
    },
    image && /* @__PURE__ */ React4.createElement(StackLayout2, { spacing: "$none", horizontalAlignment: "stretch" }, bannerText && /* @__PURE__ */ React4.createElement(
      StatusBanner,
      {
        status: "success",
        title: bannerText,
        icon: /* @__PURE__ */ React4.createElement(Icon, { icon: programHeadingBannerIcon2 }),
        css: {
          justifyContent: "center",
          borderRadius: "$small $small $none $none",
          "> div": {
            flexGrow: "0"
          }
        }
      }
    ), /* @__PURE__ */ React4.createElement(
      CoverImage,
      {
        src: image,
        alt: imageAltText != null ? imageAltText : "",
        height: PROGRAM_HEADING_FIXED_HEIGHT,
        borderRadius: "none",
        maxWidth: "100%",
        backgroundColor: "highlight.background.subdued",
        marginBottom: "twoAndHalf",
        verticalAlignment: "center"
      }
    )),
    /* @__PURE__ */ React4.createElement(
      StackLayout2,
      {
        spacing: "$oneAndHalf",
        horizontalAlignment: centerAlignHeadingItems ? "center" : "start"
      },
      badge && /* @__PURE__ */ React4.createElement(Badge, __spreadValues({}, badge)),
      /* @__PURE__ */ React4.createElement(
        StackLayout2,
        {
          css: {
            width: settingsComponent ? "100%" : "initial",
            textAlign: centerAlignHeadingItems ? "center" : "start"
          },
          orientation: "horizontal",
          horizontalAlignment: "spaceBetween",
          verticalAlignment: "center"
        },
        /* @__PURE__ */ React4.createElement(HeadingText, { level: "1", size: { "@initial": "xxl", "@mobile": "xl" } }, title),
        settingsComponent
      ),
      children
    )
  );
};

// src/components/program-sponsor/program-sponsor.component.tsx
import * as React5 from "react";
import { HeadingText as HeadingText2, StackLayout as StackLayout3 } from "@leagueplatform/genesis-core";
import { Image } from "@leagueplatform/genesis-commons";
import { useIntl as useIntl2 } from "@leagueplatform/locales";
var ProgramSponsor = ({
  image,
  sponsorName
}) => {
  const { formatMessage } = useIntl2();
  return /* @__PURE__ */ React5.createElement(StackLayout3, { spacing: "$one" }, /* @__PURE__ */ React5.createElement(
    HeadingText2,
    {
      level: "3",
      size: {
        "@initial": "xl",
        "@mobile": "lg"
      }
    },
    formatMessage({ id: "PROGRAM_CREATED_BY" })
  ), /* @__PURE__ */ React5.createElement(
    Image,
    {
      src: image,
      height: "56px",
      width: "auto",
      alt: formatMessage({ id: "SPONSOR_LOGO" }, { name: sponsorName })
    }
  ));
};

// src/components/program-rich-text/program-rich-text.component.tsx
import React6 from "react";
import { HtmlToReact } from "@leagueplatform/web-common";
import {
  Box as Box2,
  StackLayout as StackLayout4,
  TextAction,
  styled
} from "@leagueplatform/genesis-core";
var StyledH3 = styled("h3", {
  fontSize: "$headingTwo",
  marginTop: "$twoAndHalf",
  marginBottom: "$one",
  color: "$onSurfaceTextPrimary",
  fontWeight: 600,
  "@mobile": {
    fontSize: "$headingThree"
  }
});
var StyledH4 = styled("h4", {
  fontSize: "$one",
  marginTop: "$two",
  marginBottom: "$half",
  color: "$onSurfaceTextPrimary",
  fontWeight: 600
});
var StyledParagraph = styled("p", {
  lineHeight: "24px",
  fontSize: "$one",
  color: "$onSurfaceTextPrimary",
  a: {
    margin: 0
  }
});
var StyledLink = styled(TextAction, {
  fontSize: "inherit",
  lineHeight: "inherit",
  marginTop: "$one"
});
var StyledList = styled(Box2, {
  "& > li": {
    fontSize: "$one",
    color: "$onSurfaceTextPrimary"
  }
});
var RichTextOptions = {
  h3: {
    component: StyledH3
  },
  h4: {
    component: StyledH4
  },
  p: {
    component: StyledParagraph
  },
  a: {
    component: StyledLink,
    props: {
      target: "_blank"
    }
  },
  li: {
    component: StyledList,
    props: {
      as: "li"
    }
  }
};
var ProgramRichText = ({
  richText,
  options
}) => /* @__PURE__ */ React6.createElement(StackLayout4, { spacing: "$none", css: { whiteSpace: "pre-wrap" } }, /* @__PURE__ */ React6.createElement(
  HtmlToReact,
  {
    htmlString: richText,
    options: __spreadValues(__spreadValues({}, RichTextOptions), options)
  }
));

// src/components/program-limit-modal.view.jsx
import * as React7 from "react";
import { func, string } from "prop-types";
import {
  Flex,
  HeadingThree,
  BodyOne,
  PrimaryButton
} from "@leagueplatform/genesis-commons";
import { usePageViewAnalytics, PRODUCT_AREA } from "@leagueplatform/analytics";
import { useIntl as useIntl3 } from "@leagueplatform/locales";

// src/constants/health-journey-analytics.constants.ts
var SCREEN_NAMES = {
  // health journey specific
  ACTIVITY_TIMELINE: "health journey activities",
  ACTIVITY_DETAILS: "health campaign activity detail",
  COMPLETE_MODAL: "health campaign activity complete",
  REMOVE_MODAL: "health campaign activity remove",
  SAVE_PROGRESS_MODAL: "health campaign activity goal confirmation",
  EXIT_CONFIRM_MODAL: "health campaign activity exit confirmation",
  PROGRESS_PAGE: "health journey progress",
  PROGRAM_LIMIT_MODAL: "health campaign limit banner",
  SUGGESTED_GROUP: "health journey activities suggested group",
  HOME_PAGE: "homepage",
  HISTORY_PAGE: "health journey history",
  // health programs specific
  PROGRAM_LIBRARY: "health campaign library",
  CATEGORY_PAGE: "health campaign category",
  HEALTH_CAMPAIGN_REMOVAL: "health campaign removal",
  HEALTH_CAMPAIGN_DETAIL: "health campaign detail",
  HEALTH_ASSESSMENT_REPORT: "health assessment report"
};
var TAB_DISPLAY_NAMES = [
  "activities",
  "progress",
  "history",
  "explore"
];
var TAB_PANEL_ANALYTICS_NAMES = [
  SCREEN_NAMES.ACTIVITY_TIMELINE,
  SCREEN_NAMES.PROGRESS_PAGE,
  SCREEN_NAMES.HISTORY_PAGE,
  SCREEN_NAMES.PROGRAM_LIBRARY
];

// src/components/program-limit-modal.view.jsx
var ProgramLimitModal = ({ onClose, title, description }) => {
  const { formatMessage } = useIntl3();
  usePageViewAnalytics({
    product_area: PRODUCT_AREA.JOURNEY,
    screen_name: SCREEN_NAMES.PROGRAM_LIMIT_MODAL
  });
  return /* @__PURE__ */ React7.createElement(HealthActivityModal, { onClose, "aria-label": title }, /* @__PURE__ */ React7.createElement(HeadingThree, { marginBottom: "oneAndHalf" }, title), /* @__PURE__ */ React7.createElement(BodyOne, { color: "onSurface.text.subdued" }, description), /* @__PURE__ */ React7.createElement(Flex, { justifyContent: "flex-end" }, /* @__PURE__ */ React7.createElement(PrimaryButton, { onClick: () => onClose(), marginTop: "oneAndHalf" }, formatMessage({ id: "OK" }))));
};
ProgramLimitModal.propTypes = {
  onClose: func.isRequired,
  title: string.isRequired,
  description: string.isRequired
};

// src/components/transition-controller.component.jsx
import * as React8 from "react";
import { Box as Box3 } from "@leagueplatform/genesis-commons";
import { func as func2, shape, string as string2, number } from "prop-types";
import { Transition, SwitchTransition } from "react-transition-group";
var TransitionTypes = Object.freeze({
  FADE: "fade"
});
var { FADE } = TransitionTypes;
var getTransitionStyles = (type = FADE, duration = 350) => ({
  [FADE]: {
    defaultStyles: {
      transition: `opacity ${duration}ms ease-in-out`,
      opacity: 0,
      "will-change": "opacity"
    },
    transitionStyles: {
      entering: { opacity: 0 },
      entered: { opacity: 1 },
      exiting: { opacity: 0 },
      exited: { opacity: 0 }
    }
  }
})[type];
var getViewWithProps = (viewObject) => {
  var _a, _b;
  const component = { View: React8.Fragment, props: {} };
  if (!viewObject)
    return component;
  component.View = (_a = viewObject == null ? void 0 : viewObject.view) != null ? _a : viewObject;
  component.props = (_b = viewObject == null ? void 0 : viewObject.props) != null ? _b : {};
  return component;
};
var ViewTransitionController = (_a) => {
  var _b = _a, {
    handleActiveView,
    views,
    duration,
    transitionType: type
  } = _b, rest = __objRest(_b, [
    "handleActiveView",
    "views",
    "duration",
    "transitionType"
  ]);
  const { defaultStyles, transitionStyles } = getTransitionStyles(
    type,
    duration
  );
  const activeView = handleActiveView();
  const { View, props } = getViewWithProps(views == null ? void 0 : views[activeView]);
  return /* @__PURE__ */ React8.createElement(SwitchTransition, null, /* @__PURE__ */ React8.createElement(Transition, { timeout: duration, key: activeView }, (state) => (
    // eslint-disable-next-line react/jsx-props-no-spreading -- FIXME: automatically added for existing issue
    /* @__PURE__ */ React8.createElement(Box3, __spreadValues(__spreadValues(__spreadValues({}, defaultStyles), transitionStyles[state]), rest), /* @__PURE__ */ React8.createElement(View, __spreadValues({}, props)))
  )));
};
ViewTransitionController.propTypes = {
  transitionType: string2,
  duration: number,
  handleActiveView: func2.isRequired,
  views: shape({}).isRequired
};
ViewTransitionController.defaultProps = {
  transitionType: FADE,
  duration: 350
};

// src/components/program-detail-card/program-detail-card.component.tsx
import React9 from "react";
import {
  Card,
  HeadingText as HeadingText3,
  StackLayout as StackLayout5
} from "@leagueplatform/genesis-core";
var ProgramDetailCard = ({
  backgroundColor = "$surfaceBackgroundPrimary",
  heading,
  children,
  footer
}) => /* @__PURE__ */ React9.createElement(
  Card.Elevated,
  {
    css: {
      backgroundColor,
      width: "100%"
    }
  },
  /* @__PURE__ */ React9.createElement(Card.Section, null, /* @__PURE__ */ React9.createElement(StackLayout5, { spacing: "$oneAndHalf", horizontalAlignment: "stretch" }, /* @__PURE__ */ React9.createElement(HeadingText3, { level: "2", size: "md" }, heading), children)),
  footer
);

// src/components/program-banner/program-banner.component.tsx
import React10, { createElement as createElement6 } from "react";
import {
  StackLayout as StackLayout6,
  UtilityText
} from "@leagueplatform/genesis-core";
import { Image as Image2 } from "@leagueplatform/genesis-commons";
import { IconText } from "@leagueplatform/web-common-components";
var ICON_SIZE = 54;
var ProgramBanner = ({
  boldedText,
  basicText,
  icon,
  css: css2,
  horizontalAlignment = "start"
}) => /* @__PURE__ */ React10.createElement(
  StackLayout6,
  {
    orientation: "horizontal",
    verticalAlignment: "center",
    horizontalAlignment,
    css: css2
  },
  /* @__PURE__ */ React10.createElement(StackLayout6, { spacing: "$half", horizontalAlignment }, boldedText && (typeof boldedText === "string" ? /* @__PURE__ */ React10.createElement(UtilityText, { emphasis: "emphasized" }, boldedText) : createElement6(IconText, boldedText)), typeof basicText === "string" ? /* @__PURE__ */ React10.createElement(UtilityText, { css: { fontSize: "inherit", lineHeight: "inherit" } }, basicText) : basicText),
  icon && /* @__PURE__ */ React10.createElement(Image2, { src: icon, alt: "", size: ICON_SIZE })
);

// src/components/program-detail-card/program-detail-card-footer.component.tsx
import React11 from "react";
import { Card as Card2 } from "@leagueplatform/genesis-core";
var ProgramDetailCardFooter = ({
  boldedText,
  basicText,
  icon,
  backgroundColor = "$warningBackgroundSubdued"
}) => /* @__PURE__ */ React11.createElement(Card2.Section, { css: { backgroundColor } }, /* @__PURE__ */ React11.createElement(ProgramBanner, { basicText, boldedText, icon }));

// src/components/program-achievements/program-achievements.component.tsx
import React13 from "react";
import { HeadingText as HeadingText4, StackLayout as StackLayout7 } from "@leagueplatform/genesis-core";
import { useIntl as useIntl4 } from "@leagueplatform/locales";

// src/components/achievement-callout-banner/achievement-callout-banner.component.tsx
import React12 from "react";
import { Image as Image3 } from "@leagueplatform/genesis-commons";
import { Box as Box4, ParagraphText } from "@leagueplatform/genesis-core";
var AchievementCalloutBanner = ({
  image,
  text,
  badgeSize = "48px"
}) => /* @__PURE__ */ React12.createElement(Box4, { css: { display: "flex", alignItems: "center" } }, /* @__PURE__ */ React12.createElement(Image3, { src: image, height: badgeSize, alt: "" }), /* @__PURE__ */ React12.createElement(
  ParagraphText,
  {
    size: "sm",
    css: { marginInlineStart: "$one", fontWeight: 500 }
  },
  /* @__PURE__ */ React12.createElement(ProgramRichText, { richText: text })
));

// src/components/program-achievements/program-achievements.component.tsx
var ProgramAchievements = ({
  badgeUrl,
  title,
  text
}) => {
  const { formatMessage } = useIntl4();
  return /* @__PURE__ */ React13.createElement(StackLayout7, { spacing: "$one" }, /* @__PURE__ */ React13.createElement(
    HeadingText4,
    {
      level: "3",
      size: {
        "@initial": "xl",
        "@mobile": "lg"
      }
    },
    title || formatMessage({ id: "ACHIEVEMENTS" })
  ), /* @__PURE__ */ React13.createElement(AchievementCalloutBanner, { image: badgeUrl, text, badgeSize: "56px" }));
};

// src/components/program-rules/program-rules.component.tsx
import React14 from "react";
import { StackLayout as StackLayout8 } from "@leagueplatform/genesis-core";
var ProgramRules = ({ programRules }) => /* @__PURE__ */ React14.createElement(StackLayout8, { spacing: "$one" }, /* @__PURE__ */ React14.createElement(ProgramRichText, { richText: programRules }));

// src/components/composable-activity-card/composable-activity-card.tsx
import * as React15 from "react";
import {
  Card as Card3,
  HeadingText as HeadingText5,
  Icon as Icon2,
  StackLayout as StackLayout9,
  UtilityText as UtilityText2,
  useClickableCard,
  TextAction as TextAction2,
  useMediaQuery as useMediaQuery3,
  queryHelpers as queryHelpers3
} from "@leagueplatform/genesis-core";
import { Badge as Badge2 } from "@leagueplatform/web-common-components";
import {
  LinkLegacy as Link
} from "@leagueplatform/routing";
var LARGE_IMAGE_WIDTH = "130px";
var CardContentWrapper = React15.forwardRef(({ cta, children, onCardMouseDown, onCardMouseUp }, ref) => {
  if (cta) {
    return /* @__PURE__ */ React15.createElement(
      Card3.ClickArea,
      {
        ref,
        onCardMouseUp,
        onCardMouseDown
      },
      children
    );
  }
  return /* @__PURE__ */ React15.createElement("div", null, " ", children);
});
function ComposableActivityCard({
  badge,
  body,
  cta,
  eyebrow,
  footer,
  headline,
  icon,
  image,
  title,
  onClick
}) {
  const isBelowMobile = useMediaQuery3(queryHelpers3.down("mobileLandscape"));
  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } = useClickableCard();
  const withLargeImage = (image == null ? void 0 : image.shape) === "large";
  const withImage = image && image.shape !== "large";
  const displayImageAtTop = icon || isBelowMobile;
  return /* @__PURE__ */ React15.createElement(
    Card3.Elevated,
    {
      css: {
        width: "100%",
        backgroundColor: "$surfaceCardSecondary",
        borderColor: "$onSurfaceBorderSubdued"
      }
    },
    /* @__PURE__ */ React15.createElement(
      CardContentWrapper,
      {
        cta,
        ref: clickAreaRef,
        onCardMouseUp,
        onCardMouseDown
      },
      /* @__PURE__ */ React15.createElement(StackLayout9, { horizontalAlignment: "stretch" }, withLargeImage && /* @__PURE__ */ React15.createElement(
        Card3.Image,
        {
          image: image.image,
          imageAlt: image.altText,
          css: {
            display: "none",
            "@mobile": {
              display: "block"
            }
          }
        }
      ), headline, /* @__PURE__ */ React15.createElement(
        StackLayout9,
        {
          orientation: "horizontal",
          spacing: "$half",
          css: {
            paddingInlineEnd: "$oneAndHalf"
          },
          verticalAlignment: displayImageAtTop ? "top" : "center"
        },
        /* @__PURE__ */ React15.createElement(
          StackLayout9,
          {
            css: {
              paddingTop: "$one",
              paddingInlineStart: "$oneAndHalf",
              paddingBottom: "$one"
            },
            spacing: "$threeQuarters"
          },
          badge && /* @__PURE__ */ React15.createElement(
            Badge2,
            {
              css: {
                backgroundColor: badge.backgroundColor,
                borderRadius: "$small"
              }
            },
            /* @__PURE__ */ React15.createElement(React15.Fragment, null, /* @__PURE__ */ React15.createElement(
              Icon2,
              {
                icon: badge.image,
                tint: badge.imageTint,
                size: "$one"
              }
            ), /* @__PURE__ */ React15.createElement(
              UtilityText2,
              {
                size: "eyebrow",
                emphasis: "emphasized",
                css: {
                  paddingInlineStart: "$quarter"
                }
              },
              badge.body
            ))
          ),
          eyebrow,
          /* @__PURE__ */ React15.createElement(
            StackLayout9,
            {
              spacing: "$threeQuarters",
              horizontalAlignment: "stretch",
              css: {
                width: "100%",
                // If there is no body, add more padding to the title.
                paddingTop: !body ? "$half" : void 0,
                paddingBottom: !body ? "$half" : void 0
              }
            },
            /* @__PURE__ */ React15.createElement(
              HeadingText5,
              {
                size: title.size || "sm",
                level: title.level,
                emphasis: title.textEmphasis
              },
              cta ? /* @__PURE__ */ React15.createElement(
                TextAction2,
                {
                  as: Link,
                  ref: primaryActionRef,
                  to: cta,
                  css: {
                    "&&": {
                      all: "inherit"
                    }
                  },
                  onClick
                },
                title.text
              ) : title.text
            ),
            body
          ),
          footer
        ),
        icon && /* @__PURE__ */ React15.createElement(
          Icon2,
          {
            icon: icon.icon,
            tint: icon.tint,
            label: icon.label,
            css: {
              marginTop: "$one"
            }
          }
        ),
        withImage && /* @__PURE__ */ React15.createElement(
          Card3.Image,
          {
            image: image.image,
            imageAlt: image.altText,
            css: {
              height: "$five",
              width: "$five",
              borderRadius: image.shape === "circle" ? "$circle" : "$small",
              margin: "$one",
              marginInlineEnd: "$none"
            }
          }
        ),
        withLargeImage && /* @__PURE__ */ React15.createElement(
          Card3.Image,
          {
            image: image.image,
            imageAlt: image.altText,
            css: {
              height: "$five",
              width: LARGE_IMAGE_WIDTH,
              borderRadius: "$small",
              margin: "$one $none",
              "@mobile": {
                display: "none"
              }
            }
          }
        )
      ))
    )
  );
}

// src/components/composable-activity-cta-card/composable-activity-cta-card.tsx
import * as React16 from "react";
import {
  Card as Card4,
  HeadingText as HeadingText6,
  Icon as Icon3,
  StackLayout as StackLayout10,
  UtilityText as UtilityText3,
  useMediaQuery as useMediaQuery4,
  queryHelpers as queryHelpers4,
  Button,
  Box as Box5
} from "@leagueplatform/genesis-core";
import {
  LinkLegacy as Link2
} from "@leagueplatform/routing";
import { Badge as Badge3 } from "@leagueplatform/web-common-components";
var LARGE_IMAGE_WIDTH2 = "130px";
var ComposableActivityCTACardButton = ({
  href,
  text,
  onClick,
  titleId
}) => /* @__PURE__ */ React16.createElement(
  Button,
  {
    "aria-describedby": titleId,
    css: { padding: "$half $two $half", marginTop: "$none" },
    as: Link2,
    to: href,
    onClick
  },
  text
);
function ComposableActivityCTACard({
  id,
  badge,
  body,
  cta,
  eyebrow,
  footer,
  headline,
  icon,
  image,
  title
}) {
  const isBelowMobile = useMediaQuery4(queryHelpers4.down("mobileLandscape"));
  const withLargeImage = (image == null ? void 0 : image.shape) === "large";
  const withImage = image && image.shape !== "large";
  const displayImageAtTop = icon || isBelowMobile;
  const titleId = `health-journey-activity_${id}`;
  return /* @__PURE__ */ React16.createElement(
    Card4.Elevated,
    {
      css: { width: "100%", backgroundColor: "$surfaceBackgroundPrimary" }
    },
    /* @__PURE__ */ React16.createElement(StackLayout10, { horizontalAlignment: "stretch" }, withLargeImage && /* @__PURE__ */ React16.createElement(
      Card4.Image,
      {
        image: image.image,
        imageAlt: image.altText,
        css: {
          display: "none",
          "@mobile": {
            display: "block"
          }
        }
      }
    ), headline, /* @__PURE__ */ React16.createElement(
      StackLayout10,
      {
        orientation: "horizontal",
        spacing: "$half",
        css: {
          paddingInlineEnd: "$oneAndHalf"
        },
        verticalAlignment: displayImageAtTop ? "top" : "center"
      },
      /* @__PURE__ */ React16.createElement(
        StackLayout10,
        {
          css: {
            paddingTop: "$one",
            paddingInlineStart: "$oneAndHalf",
            paddingBottom: "$one"
          },
          spacing: "$threeQuarters"
        },
        badge && /* @__PURE__ */ React16.createElement(
          Badge3,
          {
            css: {
              backgroundColor: badge.backgroundColor,
              borderRadius: "$small"
            }
          },
          /* @__PURE__ */ React16.createElement(React16.Fragment, null, /* @__PURE__ */ React16.createElement(Icon3, { icon: badge.image, tint: badge.imageTint, size: "$one" }), /* @__PURE__ */ React16.createElement(
            UtilityText3,
            {
              size: "eyebrow",
              emphasis: "emphasized",
              css: {
                paddingInlineStart: "$quarter"
              }
            },
            badge.body
          ))
        ),
        eyebrow,
        /* @__PURE__ */ React16.createElement(
          StackLayout10,
          {
            spacing: "$threeQuarters",
            horizontalAlignment: "stretch",
            css: {
              width: "100%",
              // If there is no body, add more padding to the title.
              paddingTop: !body ? "$half" : void 0,
              paddingBottom: !body ? "$half" : void 0
            }
          },
          /* @__PURE__ */ React16.createElement(
            HeadingText6,
            {
              id: titleId,
              size: title.size || "sm",
              level: title.level,
              emphasis: title.textEmphasis
            },
            title.text
          ),
          body
        ),
        footer
      ),
      icon && /* @__PURE__ */ React16.createElement(
        Icon3,
        {
          icon: icon.icon,
          tint: icon.tint,
          label: icon.label,
          css: {
            marginTop: "$one"
          }
        }
      ),
      withImage && /* @__PURE__ */ React16.createElement(
        Card4.Image,
        {
          image: image.image,
          imageAlt: image.altText,
          css: {
            height: "$five",
            width: "148px",
            borderRadius: image.shape === "circle" ? "$circle" : "$small",
            margin: "$one",
            marginInlineEnd: "$none"
          }
        }
      ),
      withLargeImage && /* @__PURE__ */ React16.createElement(
        Card4.Image,
        {
          image: image.image,
          imageAlt: image.altText,
          css: {
            height: "$five",
            width: LARGE_IMAGE_WIDTH2,
            borderRadius: "$small",
            margin: "$one $none",
            "@mobile": {
              display: "none"
            }
          }
        }
      )
    ), /* @__PURE__ */ React16.createElement(
      Box5,
      {
        css: {
          width: "fillContainer",
          padding: "$none $oneAndHalf $oneAndHalf"
        }
      },
      React16.createElement(
        (cta == null ? void 0 : cta.component) || ComposableActivityCTACardButton,
        {
          text: cta.text,
          onClick: cta == null ? void 0 : cta.onClick,
          href: cta.href,
          titleId
        }
      )
    ))
  );
}

// src/components/composable-activity-card/components/activity-card-eyebrow.component.tsx
import React17 from "react";
import {
  Icon as Icon4,
  ParagraphText as ParagraphText2,
  StackLayout as StackLayout11
} from "@leagueplatform/genesis-core";
var ActivityCardEyebrow = ({
  iconProps,
  children
}) => /* @__PURE__ */ React17.createElement(
  StackLayout11,
  {
    orientation: "horizontal",
    spacing: "$quarter",
    verticalAlignment: "center"
  },
  iconProps && /* @__PURE__ */ React17.createElement(Icon4, __spreadValues({}, iconProps)),
  /* @__PURE__ */ React17.createElement(ParagraphText2, { size: "xs", emphasis: "subtle" }, children)
);

// src/components/composable-activity-card/components/activity-card-footer-item.component.tsx
import React18 from "react";
import {
  Icon as Icon5,
  UtilityText as UtilityText4,
  StackLayout as StackLayout12,
  StackItem as StackItem2
} from "@leagueplatform/genesis-core";
var ActivityCardFooterItem = ({
  iconProps,
  children
}) => /* @__PURE__ */ React18.createElement(StackItem2, null, /* @__PURE__ */ React18.createElement(
  StackLayout12,
  {
    orientation: "horizontal",
    spacing: "$half",
    verticalAlignment: "center",
    horizontalAlignment: "start"
  },
  iconProps && /* @__PURE__ */ React18.createElement(Icon5, __spreadValues({}, iconProps)),
  /* @__PURE__ */ React18.createElement(UtilityText4, { size: "sm" }, children)
));

// src/components/composable-activity-card/components/activity-card-headline.component.tsx
import React19 from "react";
import { UtilityText as UtilityText5 } from "@leagueplatform/genesis-core";
var ActivityCardHeadline = ({
  backgroundColor,
  color,
  children
}) => /* @__PURE__ */ React19.createElement(
  UtilityText5,
  {
    size: "eyebrow",
    emphasis: "emphasized",
    css: {
      backgroundColor,
      color,
      borderRadius: "$small $small 0 0",
      paddingTop: "$threeQuarters",
      paddingBottom: "$threeQuarters",
      paddingInlineStart: "$oneAndHalf",
      paddingInlineEnd: "one",
      minHeight: "40px"
    }
  },
  children
);

// src/hooks/use-help-center-links.hook.ts
import { useCallback } from "react";
import {
  getHelpCenterLink,
  GETTING_STARTED_WITH_LEAGUE_LINK,
  WHAT_ARE_CONNECTED_HEALTH_PROGRAMS_LINK,
  WHAT_DEVICES_CAN_CONNECT_LINK,
  HEALTH_PROGRAMS_MANUAL_TRACKING_LINK,
  HEALTH_PROGRAMS_AND_JOURNEY_LEARN_MORE_LINK
} from "@leagueplatform/web-common";

// src/hooks/use-current-user-active-group.hook.js
import { useQuery } from "@tanstack/react-query";
import { getGroup, GET_GROUP_MESSAGE_TYPE } from "@leagueplatform/wallet-api";
import { getActiveGroup } from "@leagueplatform/web-common";
import {
  getUserProfile,
  GET_USER_PROFILE_MESSAGE_TYPE
} from "@leagueplatform/user-profile-api";
var useCurrentUserActiveGroup = () => {
  const { data: userProfileData } = useQuery(
    [GET_USER_PROFILE_MESSAGE_TYPE],
    getUserProfile
  );
  const activeGroup = getActiveGroup(userProfileData == null ? void 0 : userProfileData.groups) || null;
  return useQuery(
    [GET_GROUP_MESSAGE_TYPE, activeGroup == null ? void 0 : activeGroup.group_id],
    () => getGroup(activeGroup == null ? void 0 : activeGroup.group_id),
    { enabled: !!(activeGroup == null ? void 0 : activeGroup.group_id) }
  );
};

// src/hooks/use-help-center-links.hook.ts
var WEARABLES_CAMPAIGN_UTM_SOURCE = "wearables-campaign";
var useHelpCenterLinks = (source = WEARABLES_CAMPAIGN_UTM_SOURCE) => {
  const { data: userGroup } = useCurrentUserActiveGroup();
  const generateHelpCenterLink = useCallback(
    (helpCenter) => getHelpCenterLink({ helpCenter, userGroup, source }),
    [source, userGroup]
  );
  return {
    whatDevicesCanConnectLink: generateHelpCenterLink(
      WHAT_DEVICES_CAN_CONNECT_LINK
    ),
    whatAreConnectedHealthProgramsLink: generateHelpCenterLink(
      WHAT_ARE_CONNECTED_HEALTH_PROGRAMS_LINK
    ),
    gettingStartedWithLeagueLink: generateHelpCenterLink(
      GETTING_STARTED_WITH_LEAGUE_LINK
    ),
    healthProgramsManualTrackingLink: generateHelpCenterLink(
      HEALTH_PROGRAMS_MANUAL_TRACKING_LINK
    ),
    healthProgramsAndJourneyLearnMoreLink: generateHelpCenterLink(
      HEALTH_PROGRAMS_AND_JOURNEY_LEARN_MORE_LINK
    )
  };
};

// src/types/api-delay-controller.types.ts
var DELAY_DISPLAY_STATES = /* @__PURE__ */ ((DELAY_DISPLAY_STATES2) => {
  DELAY_DISPLAY_STATES2[DELAY_DISPLAY_STATES2["NEVER_SHOW"] = -1] = "NEVER_SHOW";
  DELAY_DISPLAY_STATES2[DELAY_DISPLAY_STATES2["ALWAYS_SHOW"] = 0] = "ALWAYS_SHOW";
  DELAY_DISPLAY_STATES2[DELAY_DISPLAY_STATES2["SHORT_DELAY"] = 200] = "SHORT_DELAY";
  DELAY_DISPLAY_STATES2[DELAY_DISPLAY_STATES2["MEDIUM_DELAY"] = 500] = "MEDIUM_DELAY";
  return DELAY_DISPLAY_STATES2;
})(DELAY_DISPLAY_STATES || {});
var DELAY_STATUS = /* @__PURE__ */ ((DELAY_STATUS2) => {
  DELAY_STATUS2["IDLE"] = "idle";
  DELAY_STATUS2["DELAY"] = "delay";
  DELAY_STATUS2["ACTIVE"] = "active";
  DELAY_STATUS2["WAITING"] = "waiting";
  return DELAY_STATUS2;
})(DELAY_STATUS || {});

// src/constants/api-delay-config.constants.ts
var DEFAULT_API_DELAY_CONFIG = Object.freeze({
  minDuration: 3500,
  maxDuration: 1e4,
  delay: 500 /* MEDIUM_DELAY */
});

// src/hooks/use-api-delay-overlay.hook.ts
import { useQuery as useQuery2, useQueryClient } from "@tanstack/react-query";
var API_DELAY_OVERLAY_ENABLED_MESSAGE_TYPE = "api_delay_overlay_enabled";
var useApiDelayOverlay = (shouldObserveUpdates = true) => {
  const queryClient = useQueryClient();
  const { data: delayOverlayActive } = useQuery2(
    [API_DELAY_OVERLAY_ENABLED_MESSAGE_TYPE],
    () => null,
    {
      enabled: shouldObserveUpdates,
      staleTime: Infinity
    }
  );
  const setDelayOverlayActive = (isEnabled) => queryClient.setQueryData(
    [API_DELAY_OVERLAY_ENABLED_MESSAGE_TYPE],
    isEnabled
  );
  return {
    delayOverlayActive,
    setDelayOverlayActive
  };
};

// src/hooks/use-api-delay-controller.hook.ts
var { ALWAYS_SHOW, NEVER_SHOW } = DELAY_DISPLAY_STATES;
var { IDLE, DELAY, ACTIVE, WAITING } = DELAY_STATUS;
var useApiDelayController = (config = {}) => {
  const { delay, minDuration, maxDuration } = __spreadValues(__spreadValues({}, DEFAULT_API_DELAY_CONFIG), config);
  const { setDelayOverlayActive } = useApiDelayOverlay(false);
  const state = {
    status: IDLE,
    hasResponse: false,
    timers: {
      [DELAY]: {
        stopTimer: void 0,
        // Function to stop timer prematurely if it is running
        pendingTimer: void 0,
        // Reference to setTimeout promise if it is running
        activeTimer: void 0,
        // setTimeoutId if it is running
        run() {
          const handleDelay = (resolve) => {
            this.stopTimer = () => resolve(DELAY);
            const timer = window.setTimeout(() => {
              this.activeTimer = window.clearTimeout(timer);
              state.stateTransition();
              resolve(DELAY);
            }, delay);
            this.activeTimer = timer;
          };
          this.pendingTimer = new Promise(handleDelay);
        }
      },
      [ACTIVE]: {
        pendingTimer: void 0,
        // Reference to setTimeout promise if it is running
        activeTimer: void 0,
        // setTimeoutId if it is running
        run() {
          const handleMinDuration = (resolve) => {
            const timer = window.setTimeout(() => {
              this.activeTimer = window.clearTimeout(timer);
              state.stateTransition();
              resolve(ACTIVE);
            }, minDuration);
            this.activeTimer = timer;
          };
          this.pendingTimer = new Promise(handleMinDuration);
        }
      },
      [WAITING]: {
        stopTimer: void 0,
        // Function to stop timer prematurely if it is running
        pendingTimer: void 0,
        // Reference to setTimeout promise if it is running
        activeTimer: void 0,
        // setTimeoutId if it is running
        run() {
          const handleMaxDuration = (resolve) => {
            this.stopTimer = () => resolve(WAITING);
            const timer = window.setTimeout(() => {
              this.activeTimer = window.clearTimeout(timer);
              state.stateTransition();
              resolve(WAITING);
            }, maxDuration - minDuration);
            this.activeTimer = timer;
          };
          this.pendingTimer = new Promise(handleMaxDuration);
        }
      }
    },
    stateTransition() {
      const { status: currentStatus } = this;
      const nextStatus = this.getNextStatus();
      const isValidUpdate = currentStatus !== nextStatus || nextStatus === IDLE;
      if (isValidUpdate) {
        this.status = nextStatus;
        this.runActionForStatus(nextStatus);
      }
    },
    getNextStatus() {
      var _a2;
      const { timers, status, hasResponse } = this;
      const timerIsActive = (_a2 = timers[status]) == null ? void 0 : _a2.activeTimer;
      switch (status) {
        case DELAY:
          if (hasResponse)
            return IDLE;
          return ACTIVE;
        case ACTIVE:
          if (timerIsActive)
            return ACTIVE;
          if (hasResponse)
            return IDLE;
          return WAITING;
        case WAITING:
          return IDLE;
        case IDLE:
        default:
          if (hasResponse || delay === NEVER_SHOW)
            return IDLE;
          if (delay === ALWAYS_SHOW)
            return ACTIVE;
          return DELAY;
      }
    },
    runActionForStatus(status) {
      const currentTimer = this.timers[status];
      switch (status) {
        case DELAY:
        case WAITING:
          currentTimer == null ? void 0 : currentTimer.run();
          break;
        case ACTIVE:
          setDelayOverlayActive(true);
          currentTimer == null ? void 0 : currentTimer.run();
          break;
        case IDLE:
        default:
          setDelayOverlayActive(false);
          if (this.hasResponse) {
            this.handleCleanup();
          }
          break;
      }
    },
    handleCleanup() {
      Object.values(this.timers).forEach(({ stopTimer, activeTimer }) => {
        if (stopTimer) {
          stopTimer();
        }
        if (activeTimer) {
          window.clearTimeout(activeTimer);
        }
      });
    },
    waitForPendingTimers() {
      return __async(this, null, function* () {
        const pendingTimerCallback = ({ pendingTimer }) => pendingTimer;
        const pendingTimers = Object.values(this.timers).filter(pendingTimerCallback).map(pendingTimerCallback);
        return Promise.all(pendingTimers);
      });
    }
  };
  const publicInterface = {
    init() {
      state.hasResponse = false;
      state.stateTransition();
    },
    finish() {
      return __async(this, null, function* () {
        state.hasResponse = true;
        state.stateTransition();
        const status = yield state.waitForPendingTimers();
        return status;
      });
    },
    get status() {
      return state.status;
    }
  };
  return publicInterface;
};

// src/hooks/use-get-user-profile.hook.ts
import { useQuery as useQuery3 } from "@tanstack/react-query";
import camelcaseKeys from "camelcase-keys";
import {
  getUserProfile as getUserProfile2,
  GET_USER_PROFILE_MESSAGE_TYPE as GET_USER_PROFILE_MESSAGE_TYPE2
} from "@leagueplatform/user-profile-api";
var useGetUserProfile = () => useQuery3([GET_USER_PROFILE_MESSAGE_TYPE2], () => getUserProfile2(), {
  select: (data) => camelcaseKeys(data, { deep: true })
});

// src/hooks/use-back-button-routing.ts
import {
  useLocationLegacy as useLocation,
  useNavigateLegacy as useNavigate2
} from "@leagueplatform/routing";
import { useFeatureFlagQuery } from "@leagueplatform/user-profile-api";

// src/constants/feature-flag-names.constants.js
var ACHIEVEMENTS_ENABLED = "achievements_enabled";
var ENABLE_CHALLENGES = "enable_challenges";
var ENABLE_CHALLENGES_FAQ = "enable_challenges_faq";
var ENABLE_ONBOARDING = "enable_onboarding";
var VERIFIABLE_AUTOMATIC_ONLY = "verifiable_automatic_only";
var ENABLE_NEW_JOURNEY_EXPERIENCE = "enable_journey_experience";
var JOURNEY_WELLNESS_AND_REWARDS = "journey_wellness_and_rewards";
var ENABLE_V3_ACTIVITY_CARDS = "enable_v3_health_activity_cards";
var ENABLE_JOURNEY_HISTORY = "enable_journey_history";
var ENABLE_CHALLENGES_WELLNESS_AND_REWARDS = "enable_challenges_wellness_and_rewards";
var ENABLE_NEW_JOURNEY_PROGRESS = "new_journey_progress_enabled";
var REWARDS_PROGRAMS_ENABLED = "rewards_programs_enabled";
var ENABLE_CHALLENGE_ACTIVITIES_TAB = "enable_challenge_activities_tab";
var ENABLE_JOURNEY_POINTS_COUNTER_BUTTON = "enable_journey_points_counter_button";
var ENABLE_JOURNEY_VIEW_MY_PROGRESS_BUTTON = "enable_journey_view_my_progress_button";
var ENABLE_JOURNEY_VIEW_MY_PROFILE_BUTTON = "enable_journey_view_my_profile_button";

// src/hooks/use-back-button-routing.ts
var useBackButtonRouting = ({
  backButtonRoute,
  onClickFunc
}) => {
  const location = useLocation();
  const navigate = useNavigate2();
  const { data: isNewJourneyExperienceEnabled } = useFeatureFlagQuery(
    ENABLE_NEW_JOURNEY_EXPERIENCE
  );
  const appHistoryStackExists = location.key !== void 0;
  const isUserComingFromAppNewJourney = isNewJourneyExperienceEnabled && appHistoryStackExists;
  const backButtonHref = isUserComingFromAppNewJourney ? "." : backButtonRoute;
  const backButtonOnClick = (e) => {
    onClickFunc();
    if (isUserComingFromAppNewJourney) {
      e.preventDefault();
      navigate(-1);
    }
  };
  return {
    backButtonHref,
    backButtonOnClick
  };
};

// src/constants/ownership-teams.constants.ts
var JOURNEY_EXPERIENCE_TEAM = "journey-experience";

// src/constants/user-health-activities.constants.ts
var UserHealthActivityStatus = /* @__PURE__ */ ((UserHealthActivityStatus2) => {
  UserHealthActivityStatus2["ACTIVE"] = "active";
  UserHealthActivityStatus2["UPCOMING"] = "upcoming";
  UserHealthActivityStatus2["COMPLETED"] = "completed";
  UserHealthActivityStatus2["EXPIRED"] = "expired";
  UserHealthActivityStatus2["REMOVED"] = "removed";
  UserHealthActivityStatus2["ON_HOLD"] = "on-hold";
  return UserHealthActivityStatus2;
})(UserHealthActivityStatus || {});
var HEALTH_JOURNEY_ACTIVITY_STATUS = __spreadValues({
  SUSPENDED: "suspended",
  COMPLETED_TODAY: "completed_today",
  MISSED: "missed"
}, UserHealthActivityStatus);
var ComponentTypes = /* @__PURE__ */ ((ComponentTypes2) => {
  ComponentTypes2["Badge"] = "badge";
  ComponentTypes2["Banner"] = "banner";
  ComponentTypes2["Biometric"] = "biometric";
  ComponentTypes2["Blockquote"] = "block-quote";
  ComponentTypes2["ChallengeProgress"] = "challenge-progress";
  ComponentTypes2["DateInput"] = "date-input";
  ComponentTypes2["DecimalInput"] = "decimal-input";
  ComponentTypes2["FullPageHighlight"] = "full-page-highlight";
  ComponentTypes2["Header"] = "header";
  ComponentTypes2["Image"] = "image";
  ComponentTypes2["IntegerInput"] = "integer-input";
  ComponentTypes2["Link"] = "link";
  ComponentTypes2["List"] = "list";
  ComponentTypes2["MultipleSelectInput"] = "multiple-select-input";
  ComponentTypes2["PersonHeight"] = "person-height-input";
  ComponentTypes2["PersonWeight"] = "person-weight-input";
  ComponentTypes2["ProgressTracking"] = "progress-tracking";
  ComponentTypes2["RichText"] = "rich-text";
  ComponentTypes2["StringInput"] = "string-input";
  ComponentTypes2["DateTimeInput"] = "date-time-input";
  ComponentTypes2["Video"] = "video";
  return ComponentTypes2;
})(ComponentTypes || {});
var {
  DateInput,
  DecimalInput,
  IntegerInput,
  MultipleSelectInput,
  PersonHeight,
  PersonWeight,
  ProgressTracking,
  StringInput
} = ComponentTypes;
var ToolboxInteractiveComponentTypes = {
  DateInput,
  DecimalInput,
  IntegerInput,
  MultipleSelectInput,
  PersonHeight,
  PersonWeight,
  ProgressTracking,
  StringInput
};
var ACTIVITY_CAMPAIGN_TYPE = /* @__PURE__ */ ((ACTIVITY_CAMPAIGN_TYPE2) => {
  ACTIVITY_CAMPAIGN_TYPE2["OPTIN"] = "opt-in";
  ACTIVITY_CAMPAIGN_TYPE2["CHALLENGE"] = "challenge";
  ACTIVITY_CAMPAIGN_TYPE2["SUGGESTED"] = "suggested";
  ACTIVITY_CAMPAIGN_TYPE2["REWARD"] = "reward";
  return ACTIVITY_CAMPAIGN_TYPE2;
})(ACTIVITY_CAMPAIGN_TYPE || {});
var COMPLETION_METHOD_TYPES = Object.freeze({
  STEP: "step",
  BASE: "base",
  COUNTER: "counter"
});
var JOURNEY_MANAGER_ENABLED = "journey_manager";
var CONFIGURABLE_ACTIVITY_PRIORITY = /* @__PURE__ */ ((CONFIGURABLE_ACTIVITY_PRIORITY2) => {
  CONFIGURABLE_ACTIVITY_PRIORITY2["LOW"] = "Low";
  CONFIGURABLE_ACTIVITY_PRIORITY2["MEDIUM"] = "Medium";
  CONFIGURABLE_ACTIVITY_PRIORITY2["HIGH"] = "High";
  return CONFIGURABLE_ACTIVITY_PRIORITY2;
})(CONFIGURABLE_ACTIVITY_PRIORITY || {});
var CONFIGURABLE_ACTIVITY_DETAIL_COMPOSITION = /* @__PURE__ */ ((CONFIGURABLE_ACTIVITY_DETAIL_COMPOSITION2) => {
  CONFIGURABLE_ACTIVITY_DETAIL_COMPOSITION2["SIMPLE"] = "Simple";
  CONFIGURABLE_ACTIVITY_DETAIL_COMPOSITION2["STANDARD"] = "Standard";
  CONFIGURABLE_ACTIVITY_DETAIL_COMPOSITION2["DETAILED"] = "Detailed";
  return CONFIGURABLE_ACTIVITY_DETAIL_COMPOSITION2;
})(CONFIGURABLE_ACTIVITY_DETAIL_COMPOSITION || {});

// src/constants/configurable-journey-experience.constants.ts
var HEALTH_JOURNEY_MASONRY_VERSION = 0;
var HEALTH_JOURNEY_MODULE_KEY = "health_journey";
var JOURNEY_TO_DO_TAB_APP_ID = "journey_to_do_tab";
var JOURNEY_PROGRESS_TAB_APP_ID = "journey_up_next_tab";
var HEALTH_ASSESSMENT_REPORT = "health-assessment-report";
var ACHIEVEMENTS_WIDGET = "recent_user_achievements";
var CHALLENGES_LIST_WIDGET = "challenge_campaign_progress_activity_stack";
var COMPLETED_ACTIVITIES_CAROUSEL_WIDGET = "get_completed_user_health_activities";
var MISSED_ACTIVITIES_CAROUSEL_WIDGET = "get_missed_user_health_activities";
var PRIORITY_ACTIVITIES_LIST_WIDGET = "get_active_user_health_activities_by_priority";
var PRIORITY_ACTIVITIES_ACTIVITY_STACK_WIDGET = "activity_stack";
var PROGRAMS_LIST_WIDGET = "optin_campaign_progress_activity_stack";
var STANDALONE_ACTIVITIES_WIDGET = "get_active_standalone_activities_by_priority";
var CHALLENGES_AND_PROGRAMS_LIST_WIDGET = "programs_and_challenges_progress_stack";
var REWARD_PROGRAM_CAROUSEL = "reward_programs_carousel";
var CONFIGURABLE_JOURNEY_PAGE_MAX_WIDTH = "984px";

// src/index.ts
initModule();
var ApiDelayOverlay = React20.lazy(
  () => import("./journey-api-delay-overlay.component-5HL3D2UA.js")
);
var JourneyDelayModal = React20.lazy(
  () => import("./journey-api-delay-overlay.component-5HL3D2UA.js").then((module) => ({
    default: module.JourneyDelayModal
  }))
);
var WaveAnimation = React20.lazy(
  () => import("./wave-animation.component-MWSUD2M2.js")
);
export {
  ACHIEVEMENTS_ENABLED,
  ACHIEVEMENTS_WIDGET,
  ACTIVITY_CAMPAIGN_TYPE,
  AchievementCalloutBanner,
  ActivityCardEyebrow,
  ActivityCardFooterItem,
  ActivityCardHeadline,
  ApiDelayOverlay,
  CHALLENGE,
  CHALLENGES_AND_PROGRAMS_LIST_WIDGET,
  CHALLENGES_LIST_WIDGET,
  CHALLENGE_TYPES,
  CHALLENGE_TYPE_GROUP,
  CHALLENGE_TYPE_SOLO,
  COMPLETED_ACTIVITIES_CAROUSEL_WIDGET,
  COMPLETION_METHOD_TYPES,
  CONFIGURABLE_ACTIVITY_DETAIL_COMPOSITION,
  CONFIGURABLE_ACTIVITY_PRIORITY,
  CONFIGURABLE_JOURNEY_PAGE_MAX_WIDTH,
  ComponentTypes,
  ComposableActivityCTACard,
  ComposableActivityCard,
  DEFAULT_API_DELAY_CONFIG,
  DELAY_DISPLAY_STATES,
  DELAY_STATUS,
  ENABLE_CHALLENGES,
  ENABLE_CHALLENGES_FAQ,
  ENABLE_CHALLENGES_WELLNESS_AND_REWARDS,
  ENABLE_CHALLENGE_ACTIVITIES_TAB,
  ENABLE_JOURNEY_HISTORY,
  ENABLE_JOURNEY_POINTS_COUNTER_BUTTON,
  ENABLE_JOURNEY_VIEW_MY_PROFILE_BUTTON,
  ENABLE_JOURNEY_VIEW_MY_PROGRESS_BUTTON,
  ENABLE_NEW_JOURNEY_EXPERIENCE,
  ENABLE_NEW_JOURNEY_PROGRESS,
  ENABLE_ONBOARDING,
  ENABLE_V3_ACTIVITY_CARDS,
  HEALTH_ASSESSMENT_REPORT,
  HEALTH_JOURNEY_ACTIVITY_STATUS,
  HEALTH_JOURNEY_COMMON_ASSET_KEYS,
  HEALTH_JOURNEY_MASONRY_VERSION,
  HEALTH_JOURNEY_MODULE_KEY,
  HealthActivityModal,
  JOURNEY_EXPERIENCE_TEAM,
  JOURNEY_MANAGER_ENABLED,
  JOURNEY_PROGRESS_TAB_APP_ID,
  JOURNEY_ROUTES,
  JOURNEY_TO_DO_TAB_APP_ID,
  JOURNEY_WELLNESS_AND_REWARDS,
  JourneyDelayModal,
  MISSED_ACTIVITIES_CAROUSEL_WIDGET,
  PRIORITY_ACTIVITIES_ACTIVITY_STACK_WIDGET,
  PRIORITY_ACTIVITIES_LIST_WIDGET,
  PROGRAMS_LIST_WIDGET,
  ProgramAchievements,
  ProgramBanner,
  ProgramDetailCard,
  ProgramDetailCardFooter,
  ProgramHeading,
  ProgramLayout,
  ProgramLimitModal,
  ProgramRichText,
  ProgramRules,
  ProgramSponsor,
  ProgramTabs,
  REWARDS_PROGRAMS_ENABLED,
  REWARD_PROGRAM_CAROUSEL,
  SCREEN_NAMES,
  STANDALONE_ACTIVITIES_WIDGET,
  TAB_DISPLAY_NAMES,
  TAB_PANEL_ANALYTICS_NAMES,
  ToolboxInteractiveComponentTypes,
  UserHealthActivityStatus,
  VERIFIABLE_AUTOMATIC_ONLY,
  ViewTransitionController,
  WaveAnimation,
  getLocationWithReferrer,
  useApiDelayController,
  useApiDelayOverlay,
  useBackButtonRouting,
  useGetUserProfile,
  useHelpCenterLinks
};


  if (typeof window !== 'undefined' && !(typeof process !== 'undefined' && process.env?.NODE_ENV === 'test')) {
    window.leagueSDKsPresent = window.leagueSDKsPresent || [];
    window.leagueSDKsPresent['@leagueplatform/health-journey-common'] = window.leagueSDKsPresent['@leagueplatform/health-journey-common'] || [];
    window.leagueSDKsPresent['@leagueplatform/health-journey-common'].push('2.25.1');

    const numInstances = window.leagueSDKsPresent['@leagueplatform/health-journey-common'].length

    if (numInstances > 1) {
      console.error('Multiple copies (' + numInstances + ') of the @leagueplatform/health-journey-common module are loaded! This can cause issues. Ensure that your project bundle only contains one copy/version of each League SDK package.');
    }
  }
  
//# sourceMappingURL=index.js.map